import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, ArrowLeft, UploadCloud } from "lucide-react";
import { getServices, addService, editService, removeService, uploadServiceImage } from "@/api/services";
import { HtmlEditor } from "@/components/admin/HtmlEditor";
import { api } from "@/lib/supabase";

const empty = { slug: "", titleTr: "", titleEn: "", shortTr: "", shortEn: "", contentTr: "", contentEn: "", imageUrl: "", icon: "briefcase", sortOrder: 0, published: true };

const slugify = (text: string) => {
  let str = text || "";
  const mapping: { [key: string]: string } = {
    'Ş': 's', 'ş': 's',
    'Ğ': 'g', 'ğ': 'g',
    'Ç': 'c', 'ç': 'c',
    'İ': 'i', 'ı': 'i',
    'Ö': 'o', 'ö': 'o',
    'Ü': 'u', 'ü': 'u'
  };
  str = str.replace(/[ŞşĞğÇçİıÖöÜü]/g, (letter) => mapping[letter] || letter);
  return str
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
};

export default function AdminServices() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const edit = searchParams.get("edit") || undefined;
  const lang = (searchParams.get("lang") as "tr" | "en") || "tr";
  const activeLang = lang;

  const [list, setList] = useState<any[]>([]);
  const [editing, setEditing] = useState<any | null>(null);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [translating, setTranslating] = useState(false);

  const translateText = async (text: string, from: string, to: string): Promise<string> => {
    if (!text || !text.trim()) return "";
    try {
      const data = await api.post<any>('/settings/translate.php', { text, from, to });
      return data[0].map((x: any) => x[0]).join("");
    } catch (err) {
      console.error("Translation error:", err);
      throw err;
    }
  };

  const handleAutoTranslate = async (from: "tr" | "en", to: "tr" | "en") => {
    if (!editing) return;
    
    const destTitle = to === "tr" ? editing.titleTr : editing.titleEn;
    if (destTitle && destTitle.trim() && !confirm("Hedef dildeki mevcut içeriklerin üzerine yazılacak. Devam etmek istiyor musunuz?")) {
      return;
    }

    setTranslating(true);
    const toastId = toast.loading("Çeviriliyor, lütfen bekleyin...");
    try {
      const srcTitle = from === "tr" ? editing.titleTr : editing.titleEn;
      const srcDesc = from === "tr" ? editing.shortTr : editing.shortEn;
      const srcContent = from === "tr" ? editing.contentTr : editing.contentEn;

      const translateHtmlContent = async (html: string, fromLang: string, toLang: string): Promise<string> => {
        if (!html || !html.trim()) return html;
        
        const temp = document.createElement('div');
        temp.innerHTML = html;
        
        const textNodes: { node: Node; text: string }[] = [];
        const walker = document.createTreeWalker(
          temp,
          NodeFilter.SHOW_TEXT,
          null
        );
        
        let node;
        while (node = walker.nextNode()) {
          const text = node.textContent?.trim();
          if (text) {
            textNodes.push({ node, text });
          }
        }
        
        const translations: { [key: string]: string } = {};
        for (const { text } of textNodes) {
          if (!translations[text]) {
            try {
              translations[text] = await translateText(text, fromLang, toLang);
            } catch (err) {
              translations[text] = text;
            }
          }
        }
        
        textNodes.forEach(({ node, text }) => {
          if (node.textContent) {
            node.textContent = translations[text] || text;
          }
        });
        
        return temp.innerHTML;
      };

      const translatedTitle = await translateText(srcTitle, from, to);
      const translatedDesc = await translateText(srcDesc, from, to);
      const translatedContent = await translateHtmlContent(srcContent || "", from, to);

      setEditing((prev: any) => {
        if (!prev) return null;
        if (to === "en") {
          return {
            ...prev,
            titleEn: translatedTitle,
            shortEn: translatedDesc,
            contentEn: translatedContent
          };
        } else {
          return {
            ...prev,
            titleTr: translatedTitle,
            shortTr: translatedDesc,
            contentTr: translatedContent
          };
        }
      });
      
      toast.success("Çeviri başarıyla tamamlandı!", { id: toastId });
    } catch (err: any) {
      toast.error("Çeviri sırasında bir hata oluştu: " + err.message, { id: toastId });
    } finally {
      setTranslating(false);
    }
  };

  const handleFileDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      await handleUpload(file);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      await handleUpload(file);
    }
  };

  const handleUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Lütfen geçerli bir görsel dosyası seçin.');
      return;
    }

    setUploading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const base64Data = reader.result as string;
        const url = await uploadServiceImage(file);
        setEditing((prev: any) => prev ? { ...prev, imageUrl: url } : null);
        toast.success('Görsel başarıyla yüklendi!');
      } catch (err: any) {
        toast.error('Yükleme başarısız: ' + err.message);
      } finally {
        setUploading(false);
      }
    };
    reader.onerror = () => {
      toast.error('Dosya okunamadı.');
      setUploading(false);
    };
    reader.readAsDataURL(file);
  };

  const load = async () => {
    try {
      const data = await getServices(false);
      setList(data);
    } catch (error: any) {
      toast.error('Yükleme hatası');
    }
  };
  
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!edit) {
      setEditing(null);
    } else if (edit === "new") {
      setEditing({ ...empty });
    } else if (list.length > 0) {
      const found = list.find(s => s.id === edit);
      if (found) {
        // Map API response to component state (snake_case to camelCase)
        setEditing({
          id: found.id,
          slug: found.slug,
          titleTr: found.title_tr,
          titleEn: found.title_en,
          shortTr: found.short_tr,
          shortEn: found.short_en,
          contentTr: found.content_tr,
          contentEn: found.content_en,
          imageUrl: found.image_url,
          icon: found.icon,
          sortOrder: found.sort_order,
          published: found.published,
        });
      }
    }
  }, [edit, list]);

  const save = async () => {
    try {
      const payload = { ...editing };
      const isNew = !payload.id;
      if (!payload.slug) payload.slug = slugify(payload.titleTr);
      
      // camelCase -> snake_case mapping
      const apiPayload: any = {
        slug: payload.slug,
        title_tr: payload.titleTr,
        title_en: payload.titleEn,
        short_tr: payload.shortTr,
        short_en: payload.shortEn,
        content_tr: payload.contentTr,
        content_en: payload.contentEn,
        image_url: payload.imageUrl,
        icon: payload.icon,
        sort_order: payload.sortOrder,
        published: payload.published,
      };
      
      if (isNew) {
        await addService(apiPayload);
      } else {
        await editService({ id: payload.id, updates: apiPayload });
      }
      
      toast.success("Kaydedildi");
      setSearchParams({});
      load();
    } catch (error: any) {
      toast.error(error.message || "Hata oluştu");
    }
  };

  const del = async (id: string) => {
    if (!confirm("Silinsin mi?")) return;
    try {
      await removeService(id);
      toast.success("Silindi");
      load();
    } catch (error: any) {
      toast.error(error.message || "Hata oluştu");
    }
  };

  return (
    <div>
      {editing ? (
        <div className="space-y-6">
          <div className="flex justify-between items-center border-b pb-4">
            <div>
              <button
                onClick={() => setSearchParams({})}
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-2 cursor-pointer font-medium"
              >
                ← Hizmet Listesine Dön
              </button>
              <h1 className="text-2xl font-bold text-primary">
                {editing.id ? "Hizmeti Düzenle" : "Yeni Hizmet Ekle"}
              </h1>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setSearchParams({})}
                className="px-4 py-2 text-sm font-semibold rounded-lg border bg-background hover:bg-muted cursor-pointer transition-colors"
              >
                İptal
              </button>
              <button
                onClick={save}
                className="px-5 py-2 text-sm font-semibold rounded-lg bg-primary text-primary-foreground hover:bg-primary/95 cursor-pointer transition-colors shadow-sm"
              >
                Kaydet
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 flex">
              <div className="bg-card border rounded-xl p-6 shadow-2xs flex-1 flex flex-col space-y-4">
                <div className="flex justify-between items-center border-b pb-2 mb-4">
                  <div className="flex items-center gap-3">
                    <h3 className="font-bold text-slate-800 text-sm uppercase tracking-wider text-muted-foreground">Dil Ayarlı İçerik Tanımları</h3>
                    <button
                      type="button"
                      onClick={() => handleAutoTranslate(activeLang, activeLang === "tr" ? "en" : "tr")}
                      disabled={translating}
                      className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-lg bg-primary/10 text-primary hover:bg-primary/20 disabled:opacity-50 transition-colors cursor-pointer"
                    >
                      {translating ? (
                        <>
                          <div className="w-3.5 h-3.5 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                          Çeviriliyor...
                        </>
                      ) : activeLang === "tr" ? (
                        "Otomatik Çevir (TR ➔ EN)"
                      ) : (
                        "Otomatik Çevir (EN ➔ TR)"
                      )}
                    </button>
                  </div>
                  <div className="flex bg-muted p-1 rounded-lg border text-xs gap-1">
                    <button
                      onClick={() => setSearchParams({ edit: edit || "", lang: "tr" })}
                      className={`px-3 py-1 rounded-md font-semibold transition-all cursor-pointer ${
                        activeLang === "tr"
                          ? "bg-background text-foreground shadow-2xs border"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      Türkçe (TR)
                    </button>
                    <button
                      onClick={() => setSearchParams({ edit: edit || "", lang: "en" })}
                      className={`px-3 py-1 rounded-md font-semibold transition-all cursor-pointer ${
                        activeLang === "en"
                          ? "bg-background text-foreground shadow-2xs border"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      İngilizce (EN)
                    </button>
                  </div>
                </div>

                {activeLang === "tr" ? (
                  <div className="space-y-4 flex-1 flex flex-col">
                    <Row label="Hizmet Adı (Türkçe)">
                      <input
                        className="inp"
                        value={editing.titleTr}
                        onChange={e => {
                          const newTitle = e.target.value;
                          const updates: any = { titleTr: newTitle };
                          if (!editing.id) {
                            updates.slug = slugify(newTitle);
                          }
                          setEditing({ ...editing, ...updates });
                        }}
                      />
                    </Row>

                    <Row label="Kısa Açıklama / Özet (Türkçe)">
                      <textarea
                        className="inp min-h-[80px] resize-y"
                        rows={2}
                        value={editing.shortTr ?? ""}
                        onChange={e => setEditing({ ...editing, shortTr: e.target.value })}
                      />
                    </Row>

                    <Row label="Detaylı İçerik (Türkçe)" className="flex-1">
                      <HtmlEditor
                        value={editing.contentTr ?? ""}
                        onChange={val => setEditing({ ...editing, contentTr: val })}
                        className="flex-1"
                      />
                    </Row>
                  </div>
                ) : (
                  <div className="space-y-4 flex-1 flex flex-col">
                    <Row label="Hizmet Adı (İngilizce)">
                      <input
                        className="inp"
                        value={editing.titleEn}
                        onChange={e => setEditing({ ...editing, titleEn: e.target.value })}
                      />
                    </Row>

                    <Row label="Kısa Açıklama / Özet (İngilizce)">
                      <textarea
                        className="inp min-h-[80px] resize-y"
                        rows={2}
                        value={editing.shortEn ?? ""}
                        onChange={e => setEditing({ ...editing, shortEn: e.target.value })}
                      />
                    </Row>

                    <Row label="Detaylı İçerik (İngilizce)" className="flex-1">
                      <HtmlEditor
                        value={editing.contentEn ?? ""}
                        onChange={val => setEditing({ ...editing, contentEn: val })}
                        className="flex-1"
                      />
                    </Row>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-card border rounded-xl p-6 shadow-2xs space-y-5">
                <h3 className="font-bold text-slate-800 border-b pb-2 text-sm uppercase tracking-wider text-muted-foreground">Sistem & Görünüm Ayarları</h3>

                <Row label="Adres Uzantısı (Slug / URL)">
                  <input
                    className="inp font-mono text-xs"
                    value={editing.slug}
                    onChange={e => setEditing({ ...editing, slug: e.target.value })}
                    placeholder="otomatik-olusturulur"
                  />
                </Row>

                <Row label="Hizmet İkonu">
                  <select
                    className="inp cursor-pointer"
                    value={editing.icon ?? "briefcase"}
                    onChange={e => setEditing({ ...editing, icon: e.target.value })}
                  >
                    <option value="briefcase">Çanta / Briefcase</option>
                    <option value="home">Ev / Home</option>
                    <option value="shield">Güvenlik / Shield</option>
                    <option value="id-card">Kimlik / ID Card</option>
                    <option value="file-check">Belge / File Check</option>
                    <option value="globe">Küresel / Globe</option>
                  </select>
                </Row>

                <div>
                  <label className="block text-sm font-medium mb-1">Hizmet Görseli</label>
                  
                  <div
                    onDragOver={e => {
                      e.preventDefault();
                      setDragging(true);
                    }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={handleFileDrop}
                    onClick={() => document.getElementById("file-upload-input")?.click()}
                    className={`border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-all flex flex-col items-center justify-center min-h-[120px] ${
                      dragging
                        ? "border-primary bg-primary/5 text-primary"
                        : "border-muted-foreground/20 hover:border-primary/50 bg-background text-muted-foreground"
                    }`}
                  >
                    <input
                      id="file-upload-input"
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                    />

                    {uploading ? (
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                        <span className="text-xs font-semibold">Görsel yükleniyor...</span>
                      </div>
                    ) : editing.imageUrl ? (
                      <div className="space-y-2 w-full flex flex-col items-center">
                        <div className="relative group w-full rounded-lg overflow-hidden border bg-muted aspect-video">
                          <img
                            src={editing.imageUrl}
                            alt="Hizmet Görseli"
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                            <span className="text-[11px] text-white font-bold">Görseli Değiştir</span>
                          </div>
                        </div>
                        <span className="text-[10px] text-muted-foreground break-all line-clamp-1">
                          {editing.imageUrl}
                        </span>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-1.5 p-2">
                        <div className="w-8 h-8 rounded-full bg-accent/50 flex items-center justify-center text-muted-foreground mb-1">
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4.5 h-4.5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 16.5V9.75m0 0 3 3m-3-3-3 3M6.75 19.5a4.5 4.5 0 0 1-1.41-8.775 5.25 5.25 0 0 1 10.233-2.33 3 3 0 0 1 3.758 3.848A3.752 3.752 0 0 1 18 19.5H6.75Z" />
                          </svg>
                        </div>
                        <span className="text-xs font-semibold text-foreground">Görseli sürükle bırak veya seç</span>
                        <span className="text-[10px] text-muted-foreground">PNG, JPG, WebP veya SVG (En fazla 10MB)</span>
                      </div>
                    )}
                  </div>

                  <div className="mt-2.5">
                    <input
                      className="inp text-xs font-mono"
                      value={editing.imageUrl ?? ""}
                      onChange={e => setEditing({ ...editing, imageUrl: e.target.value })}
                      placeholder="Görsel URL / Dosya yolu (Örn: /images/hizmet.jpg)"
                    />
                  </div>
                </div>

                <Row label="Görünüm Sıralaması">
                  <input
                    type="number"
                    className="inp"
                    value={editing.sortOrder}
                    onChange={e => setEditing({ ...editing, sortOrder: Number(e.target.value) })}
                  />
                </Row>

                <div className="pt-2">
                  <label className="flex items-center gap-3 p-3 rounded-lg border bg-accent/10 hover:bg-accent/20 cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-primary rounded border-gray-300 focus:ring-primary cursor-pointer"
                      checked={editing.published}
                      onChange={e => setEditing({ ...editing, published: e.target.checked })}
                    />
                    <div>
                      <span className="text-sm font-semibold block text-slate-800">Hizmet Yayında</span>
                      <span className="text-xs text-muted-foreground">Ziyaretçiler bu hizmeti görebilir</span>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-primary">Hizmetler</h1>
            <button
              onClick={() => setSearchParams({ edit: "new" })}
              className="bg-primary text-primary-foreground px-4 py-2 rounded-md flex items-center gap-2 cursor-pointer font-medium hover:bg-primary/95 transition-colors shadow-sm"
            >
              <Plus size={16} /> Yeni Hizmet Ekle
            </button>
          </div>
          <div className="bg-card border rounded-xl overflow-hidden shadow-2xs">
            <table className="w-full text-sm">
              <thead className="bg-accent/50 border-b">
                <tr>
                  <th className="text-left p-3 text-xs font-bold text-muted-foreground uppercase tracking-wider">Sıra</th>
                  <th className="text-left p-3 text-xs font-bold text-muted-foreground uppercase tracking-wider">Başlık (TR)</th>
                  <th className="text-left p-3 text-xs font-bold text-muted-foreground uppercase tracking-wider">Slug</th>
                  <th className="text-left p-3 text-xs font-bold text-muted-foreground uppercase tracking-wider">Durum</th>
                  <th className="text-right p-3 text-xs font-bold text-muted-foreground uppercase tracking-wider">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {list.map(s => {
                  const hasTr = !!(s.title_tr?.trim() && s.content_tr?.trim());
                  const hasEn = !!(s.title_en?.trim() && s.content_en?.trim());

                  return (
                    <tr key={s.id} className="border-t hover:bg-accent/5 transition-colors">
                      <td className="p-3 font-medium text-slate-600">{s.sort_order}</td>
                      <td className="p-3 font-semibold text-slate-800">{s.title_tr}</td>
                      <td className="p-3 text-muted-foreground font-mono text-xs">{s.slug}</td>
                      <td className="p-3">
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${s.published ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}>
                          {s.published ? "Yayında" : "Taslak"}
                        </span>
                      </td>
                      <td className="p-3 text-right">
                        <div className="inline-flex items-center gap-1.5 mr-2">
                          <button
                            onClick={() => setSearchParams({ edit: s.id, lang: "tr" })}
                            className={`px-2 py-0.5 rounded text-[11px] font-bold border transition-colors cursor-pointer ${
                              hasTr
                                ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
                                : "bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100"
                            }`}
                            title={hasTr ? "Türkçe İçerik Tamamlandı (Düzenlemek için tıklayın)" : "Türkçe İçerik EKSİK (Eklemek için tıklayın)"}
                          >
                            TR
                          </button>
                          <button
                            onClick={() => setSearchParams({ edit: s.id, lang: "en" })}
                            className={`px-2 py-0.5 rounded text-[11px] font-bold border transition-colors cursor-pointer ${
                              hasEn
                                ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
                                : "bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100"
                            }`}
                            title={hasEn ? "İngilizce İçerik Tamamlandı (Düzenlemek için tıklayın)" : "İngilizce İçerik EKSİK (Eklemek için tıklayın)"}
                          >
                            EN
                          </button>
                        </div>

                        <button
                          onClick={() => setSearchParams({ edit: s.id, lang: "tr" })}
                          className="p-1.5 hover:bg-accent rounded text-slate-600 hover:text-foreground cursor-pointer transition-colors inline-block align-middle"
                          title="Düzenle"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                            <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                          </svg>
                        </button>
                        <button
                          onClick={() => del(s.id)}
                          className="p-1.5 hover:bg-destructive/10 text-destructive rounded cursor-pointer transition-colors inline-block align-middle"
                          title="Sil"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <style>{`
        .inp {
          width: 100%;
          border: 1px solid var(--border);
          border-radius: 8px;
          padding: 10px 12px;
          background: var(--background);
          font-size: 0.875rem;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .inp:focus {
          outline: none;
          border-color: var(--primary);
          box-shadow: 0 0 0 2px rgba(var(--primary-rgb), 0.15);
        }
      `}</style>
    </div>
  );
}

function Row({ label, children, className = "", ...props }: any) {
  return (
    <div className={`flex flex-col ${className}`} {...props}>
      <label className="block text-sm font-medium mb-1">{label}</label>
      {children}
    </div>
  );
}
